import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  alpha
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

export interface ActionMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  color?: "inherit" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
  disabled?: boolean;
  dividerAfter?: boolean;
}

interface ActionMenuProps {
  items: ActionMenuItem[];
  iconButtonSize?: "small" | "medium" | "large";
  icon?: React.ReactNode;
  menuMinWidth?: number;
}

/**
 * A reusable action menu component that renders an icon button with a dropdown menu.
 * Supports multiple configurable menu items with icons, colors, and dividers.
 */
const ActionMenu = ({
  items,
  iconButtonSize = "small",
  icon,
  menuMinWidth = 120
}: ActionMenuProps): React.ReactElement => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleItemClick = (item: ActionMenuItem): void => {
    handleClose();
    item.onClick();
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center" }}>
      <IconButton size={iconButtonSize} onClick={handleClick} sx={{ color: "text.secondary" }}>
        {icon ?? <MoreVertIcon />}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              minWidth: menuMinWidth,
              mt: 0.5,
              borderRadius: 2,
              p: 1
            }
          }
        }}
      >
        {items.flatMap((item, index) => {
          const menuItem = (
            <MenuItem
              key={`menu-item-${index}`}
              onClick={() => {
                handleItemClick(item);
              }}
              disabled={item.disabled}
              sx={(theme) => {
                const colorKey = item.color && item.color !== "inherit" ? item.color : undefined;
                const mainColor = colorKey
                  ? theme.palette[colorKey].main
                  : theme.palette.text.primary;
                const baseBg = colorKey
                  ? alpha(theme.palette[colorKey].main, 0.1)
                  : alpha(theme.palette.text.primary, 0.05);
                const hoverBg = colorKey
                  ? alpha(theme.palette[colorKey].main, 0.2)
                  : alpha(theme.palette.text.primary, 0.1);

                return {
                  borderRadius: 1,
                  mb: index < items.length - 1 ? 0.5 : 0,
                  color: mainColor,
                  border: "1px solid",
                  borderColor: colorKey ? theme.palette[colorKey].main : theme.palette.divider,
                  backgroundColor: baseBg,
                  "&:hover": {
                    backgroundColor: hoverBg
                  }
                };
              }}
            >
              {!!item.icon && (
                <ListItemIcon
                  sx={{
                    color: item.color ? `${item.color}.main` : "inherit",
                    minWidth: 24
                  }}
                >
                  {item.icon}
                </ListItemIcon>
              )}
              <ListItemText primaryTypographyProps={{ variant: "body2", fontWeight: 500 }}>
                {item.label}
              </ListItemText>
            </MenuItem>
          );

          if (item.dividerAfter === true && index < items.length - 1) {
            return [menuItem, <Divider key={`divider-${index}`} sx={{ my: 0.5 }} />];
          }
          return [menuItem];
        })}
      </Menu>
    </Box>
  );
};

export default ActionMenu;
